import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';

import { Text } from 'modules/common';
import { useAnimateOnMount } from 'modules/common/hooks';

function AccordionItem({
  isOpen,
  children,
  label,
  description,
  leadingItem,
  trailingItem,
  withChevron,
  id,
  onClick,
  childrenClassName,
  withChildrenPadding,
  isInsideCardPadding,
  containerClassName,
  disabled,
  isVisible,
  isHoverDark,
  withOnMountAnimate,
  className,
}) {
  const onMountClassName = useAnimateOnMount();

  if (!isVisible) return null;

  return (
    <div
      className={cn('flex flex-col bg-white', className, {
        [onMountClassName]: withOnMountAnimate,
      })}
    >
      <button
        type="button"
        className={cn(
          'flex items-center gap-ds-12 text-left p-ds-16',
          containerClassName,
          {
            [onMountClassName]: withOnMountAnimate,
            'hover:bg-light': !isHoverDark,
            'hover:bg-dark': isHoverDark,
            'px-ds-24': isInsideCardPadding,
            'bg-light': isOpen && !isHoverDark,
            'bg-dark': isOpen && isHoverDark,
            '!bg-dark hover:!bg-dark': disabled,
          }
        )}
        onClick={() => onClick(id)}
        disabled={disabled}
      >
        <div className="flex items-center gap-ds-8 flex-1">
          {leadingItem && leadingItem}

          <div className="flex flex-col gap-ds-4">
            <Text weight="medium">{label}</Text>

            {description && (
              <Text type="caption" color="muted">
                {description}
              </Text>
            )}
          </div>
        </div>

        <div className="flex items-center gap-ds-8">
          {trailingItem && <div>{trailingItem}</div>}

          {withChevron && (
            <>
              {isOpen ? (
                <HiChevronUp className="h-5 w-5" />
              ) : (
                <HiChevronDown className="h-5 w-5" />
              )}
            </>
          )}
        </div>
      </button>

      <div
        className={cn(
          'text-body border-t',
          'transition-all duration-250 ease-in-out',
          {
            'p-ds-16': withChildrenPadding,
            'h-auto overflow-y-auto overflow-x-hidden opacity-100': isOpen,
            'py-ds-16': isOpen && withChildrenPadding,
            'h-0 overflow-hidden py-0 opacity-0': !isOpen,
            'px-ds-24': isInsideCardPadding && withChildrenPadding,
          },
          childrenClassName
        )}
      >
        {children}
      </div>
    </div>
  );
}

const prop = PropTypes.oneOfType([
  PropTypes.bool,
  PropTypes.element,
  PropTypes.string,
  PropTypes.instanceOf(Object),
  PropTypes.instanceOf(Function),
]);

AccordionItem.defaultProps = {
  withChevron: true,
  isOpen: false,
  onClick: () => {},
  leadingItem: false,
  trailingItem: false,
  isVisible: true,
  children: false,
  isInsideCardPadding: false,
  withChildrenPadding: true,
  disabled: false,
  isHoverDark: false,
  withOnMountAnimate: false,
  label: '',
  id: '',
  containerClassName: '',
  childrenClassName: '',
  className: '',
  description: '',
};

AccordionItem.propTypes = {
  withChevron: PropTypes.bool,
  disabled: PropTypes.bool,
  isHoverDark: PropTypes.bool,
  withOnMountAnimate: PropTypes.bool,
  isVisible: PropTypes.bool,
  isOpen: PropTypes.bool,
  isInsideCardPadding: PropTypes.bool,
  withChildrenPadding: PropTypes.bool,
  id: PropTypes.string,
  containerClassName: PropTypes.string,
  description: PropTypes.string,
  childrenClassName: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.instanceOf(Function),
  leadingItem: prop,
  trailingItem: prop,
  children: prop,
  label: prop,
};

export default AccordionItem;
